import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatEntities, ChatPhoto } from '../../api/chat';
import { ShowListing } from '../DirectoryListings/ShowListing/ShowListing';
import { BandListing } from '../DirectoryListings/BandListing/BandListing';
import { VenueListing } from '../DirectoryListings/VenueListing/VenueListing';
import { MusicianListing } from '../DirectoryListings/MusicianListing/MusicianListing';
import './ChatEntityCards.css';

// Format date to "Day Month Date" format (e.g., "Wed Sep 17")
const formatDate = (dateString: string) => {
    // Parse date parts manually to avoid timezone issues with date-only strings
    const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
    });
};

interface PhotoGridProps {
    photos: ChatPhoto[];
}

const PhotoGrid: React.FC<PhotoGridProps> = ({ photos }) => {
    const [selectedPhoto, setSelectedPhoto] = useState<ChatPhoto | null>(null);

    return (
        <>
            <div className="chat-photo-grid">
                {photos.map((photo) => (
                    <div
                        key={photo.id}
                        className="chat-photo-item"
                        onClick={() => setSelectedPhoto(photo)}
                    >
                        <img
                            src={photo.thumbnail_url || photo.url}
                            alt={photo.band_name || photo.venue_name || 'Photo'}
                            loading="lazy"
                        />
                        {(photo.band_name || photo.venue_name) && (
                            <span className="chat-photo-caption">
                                {photo.band_name || photo.venue_name}
                            </span>
                        )}
                    </div>
                ))}
            </div>
            {selectedPhoto && (
                <div className="chat-photo-modal" onClick={() => setSelectedPhoto(null)}>
                    <div className="chat-photo-modal-content" onClick={(e) => e.stopPropagation()}>
                        <img src={selectedPhoto.url} alt={selectedPhoto.band_name || selectedPhoto.venue_name || 'Photo'} />
                        <button className="chat-photo-modal-close" onClick={() => setSelectedPhoto(null)}>×</button>
                    </div>
                </div>
            )}
        </>
    );
};

interface ChatEntityCardsProps {
    entities: ChatEntities;
    onEntitySelect?: (entityType: string, entityId: string, entityName: string) => void;
}

const ChatEntityCards: React.FC<ChatEntityCardsProps> = ({ entities, onEntitySelect }) => {
    const navigate = useNavigate();
    const primary = entities.primaryEntity;

    // Count total entities to determine if this is a multi-result selection
    const totalEntities =
        entities.shows.length +
        entities.bands.length +
        entities.venues.length +
        (entities.musicians?.length || 0);

    const isMultiResult = totalEntities > 1;

    // Handle entity click - either continue chat or navigate
    const handleEntityClick = (entityType: string, entityId: string, entityName: string, route: string) => {
        if (isMultiResult && onEntitySelect) {
            // Multiple results - continue the conversation with selection
            onEntitySelect(entityType, entityId, entityName);
        } else {
            // Single result - navigate directly
            navigate(route);
        }
    };

    // Determine which entities to show based on primaryEntity
    const showShows = primary === 'shows' || (!primary && entities.shows.length > 0);
    const showBands = primary === 'bands';
    const showVenues = primary === 'venues';
    const showMusicians = primary === 'musicians';
    const showPhotos = primary === 'photos';

    const hasEntities =
        (showShows && entities.shows.length > 0) ||
        (showBands && entities.bands.length > 0) ||
        (showVenues && entities.venues.length > 0) ||
        (showMusicians && entities.musicians?.length > 0) ||
        (showPhotos && entities.photos?.length > 0);

    if (!hasEntities) {
        return null;
    }

    return (
        <div className="chat-entity-cards">
            {showShows && entities.shows.length > 0 && (
                <div className="chat-entity-section">
                    {entities.shows.map((show) => {
                        const showName = show.band || show.band_name || 'Unknown Band';
                        return (
                            <ShowListing
                                key={show.id}
                                bandName={showName}
                                imageUrl={show.band_photo_url}
                                soundcloudUrl={show.band_soundcloud_url}
                                venue={show.venue || show.venue_name || 'TBA'}
                                date={show.date ? formatDate(show.date) : ''}
                                time={show.time}
                                description={show.description}
                                onClick={() => handleEntityClick('show', show.id, `${showName} at ${show.venue || show.venue_name || 'TBA'}`, `/shows/${show.id}`)}
                            />
                        );
                    })}
                </div>
            )}

            {showBands && entities.bands.length > 0 && (
                <div className="chat-entity-section">
                    {entities.bands.map((band) => (
                        <BandListing
                            key={band.id}
                            name={band.name}
                            imageUrl={band.photo_url}
                            soundcloudUrl={band.soundcloud_url}
                            genres={band.genres}
                            onClick={() => handleEntityClick('band', band.id, band.name, `/bands/${band.id}`)}
                        />
                    ))}
                </div>
            )}

            {showVenues && entities.venues.length > 0 && (
                <div className="chat-entity-section">
                    {entities.venues.map((venue) => (
                        <VenueListing
                            key={venue.id}
                            name={venue.name}
                            location={venue.address || (venue.city && venue.state ? `${venue.city}, ${venue.state}` : '')}
                            imageUrl={venue.photo_url}
                            onClick={() => handleEntityClick('venue', venue.id, venue.name, `/venues/${venue.id}`)}
                        />
                    ))}
                </div>
            )}

            {showMusicians && entities.musicians && entities.musicians.length > 0 && (
                <div className="chat-entity-section">
                    {entities.musicians.map((musician) => (
                        <MusicianListing
                            key={musician.id}
                            name={musician.name}
                            imageUrl={musician.photo_url}
                            onClick={() => handleEntityClick('musician', musician.id, musician.name, `/musicians/${musician.id}`)}
                        />
                    ))}
                </div>
            )}

            {showPhotos && entities.photos && entities.photos.length > 0 && (
                <div className="chat-entity-section chat-photos-section">
                    <PhotoGrid photos={entities.photos} />
                </div>
            )}
        </div>
    );
};

export default ChatEntityCards;
